import moment from "moment";
import React from "react";
import { convertToPresentableText } from "../../../utils/functions";
import { InterpretEngagementLevel, InterpretLeadStatus } from "../../../utils/ui-interpreters";
import Rating from "../../../components/rating";
import { MinusIcon } from "lucide-react";
import { Chip } from "@material-tailwind/react";

const LeadReadOnlyView = ({ lead }) => {
    return (
        <div>
            <div className="w-full">
                {/* Header */}
                <div className="flex justify-between items-center px-6">
                    <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                            {lead.firstName?.charAt(0) || lead.fullName?.charAt(0) || ''}
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">
                                {lead.firstName} {lead.lastName || ''}
                                {!lead.firstName && lead.fullName}
                            </h2>
                            <div className="flex items-center text-sm text-gray-600 space-x-2">
                                <span>{lead.jobTitle}</span>
                                {lead.jobTitle && lead.companyName && <span>•</span>}
                                <span>{lead.companyName}</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        {InterpretLeadStatus(lead.leadStatus)}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Contact Information */}
                        <div className="bg-green-50 rounded-lg p-5">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Contact Information</h3>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Email</p>
                                        <p className="text-sm">{lead.emailAddress || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Phone</p>
                                        <p className="text-sm">{lead.phoneNumber || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Mobile</p>
                                        <p className="text-sm">{lead.mobileNumber || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">LinkedIn</p>
                                        <p className="text-sm">
                                            {lead.linkedinProfile ? (
                                                <a href={lead.linkedinProfile} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                    View Profile
                                                </a>
                                            ) : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                {lead.cnic && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">CNIC</p>
                                        <p className="text-sm">{lead.cnic}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Company Information */}
                        <div className="bg-gray-50 rounded-lg p-5">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Company Information</h3>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Company</p>
                                        <p className="text-sm">{lead.companyName || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Industry</p>
                                        <p className="text-sm">{lead.industry || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Size</p>
                                        <p className="text-sm">{lead.companySize || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Annual Revenue</p>
                                        <p className="text-sm">{lead.annualRevenue || 'N/A'}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Website</p>
                                    <p className="text-sm">
                                        {lead.companyWebsite ? (
                                            <a href={lead.companyWebsite} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                {lead.companyWebsite}
                                            </a>
                                        ) : 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Lead Information */}
                        <div className="bg-gray-50 rounded-lg p-5">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Lead Details</h3>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Source</p>
                                        <p className="text-sm">{convertToPresentableText(lead.leadSource?.replace(/_/g, ' ') || 'N/A')}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Created Date</p>
                                        <p className="text-sm">{moment(lead.createdDate).format("lll")}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">First Contact</p>
                                        <p className="text-sm">{moment(lead.connectionDate).format("lll")}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Last Contact</p>
                                        <p className="text-sm">{moment(lead.lastUpdated).format("lll")}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Priority</p>
                                        <div className="flex items-center">
                                            {InterpretEngagementLevel(lead.followUpPriority)}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Assigned To</p>
                                        <p className="text-sm">{lead.assignedSalesRep?.name || 'Unassigned'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Qualification */}
                        <div className="bg-gray-50 rounded-lg p-5">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Qualification</h3>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Budget Range</p>
                                        <p className="text-sm">{lead.budgetRange || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Decision Timeline</p>
                                        <p className="text-sm">{lead.decisionTimeline || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Decision Maker</p>
                                        <p className="text-sm">{lead.isDecisionMaker ? 'Yes' : 'No'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Score</p>
                                        <div className="flex items-center gap-1 font-bold">
                                            <Rating rating={lead.score / 20} /> <MinusIcon size={12} /> {lead.score} <div className="text-xs font-thin text-gray-900">{lead.companyName}</div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Purchase Intent</p>
                                    <div className="flex items-center">
                                        <Chip color="blue" value={lead.purchaseIntent || 'Not specified'} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Next Action & Requirements */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        {/* Next Action */}
                        <div className="bg-gray-50 rounded-lg p-5">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Next Steps</h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Next Action</p>
                                    <p className="text-sm">{lead.nextAction || 'No action set'}</p>
                                </div>
                                {lead.actionDueDate && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Due Date</p>
                                        <p className="text-sm">{moment(lead.actionDueDate).format("DD-MM-YYYY")}</p>
                                    </div>
                                )}
                                {lead.followUpNotes && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Follow-up Notes</p>
                                        <p className="text-sm">{lead.followUpNotes}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Requirements */}
                        <div className="bg-gray-50 rounded-lg p-5">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Requirements & Interests</h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Pain Points</p>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {lead.painPoints && lead.painPoints.length > 0 ? (
                                            lead.painPoints.map((point, index) => (
                                                <span key={index} className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-0.5 rounded">
                                                    {point}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-sm text-gray-500">None specified</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Solution Interest</p>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {lead.solutionInterest && lead.solutionInterest.length > 0 ? (
                                            lead.solutionInterest.map((interest, index) => (
                                                <span key={index} className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">
                                                    {interest}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-sm text-gray-500">None specified</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Engagement History & Notes */}
                    <div className="grid grid-cols-1 gap-6 mt-6">
                        {/* Recent Engagements */}
                        {lead.engagementHistory && lead.engagementHistory.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-5">
                                <h3 className="text-lg font-medium text-gray-900 mb-3">
                                    Recent Engagements
                                    <span className="ml-2 text-sm text-gray-500 font-normal">
                                        (showing {lead.engagementHistory.length} of {lead.engagementHistory.length})
                                    </span>
                                </h3>
                                <div className="space-y-4">
                                    {lead.engagementHistory.map((engagement, index) => (
                                        <div key={index} className="border-l-2 border-blue-500 pl-3 pb-3">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {convertToPresentableText(engagement.contactMethod.replace(/_/g, ' '))}
                                                        {engagement.by && <span className="font-normal"> by {engagement.by?.name || 'N/A'}</span>}
                                                        <span className="text-xs text-gray-500"> On {moment(engagement.date).format("lll")}</span>
                                                    </p>
                                                </div>
                                                {engagement.followUpRequired && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        Follow-up
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-1 text-sm text-gray-700">Notes: {engagement.notes}</p>
                                            <p className="mt-1 text-sm text-gray-700">Outcome: {engagement.outcome}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {lead.notes && (
                            <div className="bg-gray-50 rounded-lg p-5">
                                <h3 className="text-lg font-medium text-gray-900 mb-3">Notes / Comments</h3>
                                <div className="p-3 bg-white rounded border border-gray-200">
                                    <p className="text-sm text-gray-700 whitespace-pre-line">
                                        {lead.notes}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* GDPR Compliance */}
                        <div className="bg-gray-50 rounded-lg p-5">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Consent Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Communication Consent</p>
                                    <p className="text-sm">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${lead.communicationConsent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {lead.communicationConsent ? 'Granted' : 'Not Granted'}
                                        </span>
                                        {lead.consentDate && ` on ${moment(lead.consentDate).format("DD-MM-YYYY")}`}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Communication Preferences</p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {lead.communicationPreferences ? (
                                            <>
                                                {lead.communicationPreferences.email &&
                                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">Email</span>}
                                                {lead.communicationPreferences.phone &&
                                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">Phone</span>}
                                                {lead.communicationPreferences.sms &&
                                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">SMS</span>}
                                                {lead.communicationPreferences.directMail &&
                                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">Mail</span>}
                                                {lead.communicationPreferences.whatsapp &&
                                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">WhatsApp</span>}
                                                {!lead.communicationPreferences.email &&
                                                    !lead.communicationPreferences.phone &&
                                                    !lead.communicationPreferences.sms &&
                                                    !lead.communicationPreferences.directMail &&
                                                    !lead.communicationPreferences.whatsapp &&
                                                    <span className="text-sm text-gray-500">None specified</span>}
                                            </>
                                        ) : (
                                            <span className="text-sm text-gray-500">None specified</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadReadOnlyView;